import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  styled,
} from "@mui/material";
import type {
  Control,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  UseFormRegister,
} from "react-hook-form";
import { Controller } from "react-hook-form";

const RadioContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
});

const InputLabel = styled("label")(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const PaddedRadioGroup = styled(RadioGroup)({
  paddingInline: "8px",
});

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: theme.palette.text.secondary,
}));

const Description = styled(HelperText)({
  paddingTop: "4px",
});

const RelativeContainer = styled(Box)({
  position: "relative",
});

const ErrorMessage = styled("span")(({ theme }) => ({
  position: "absolute",
  fontSize: "12px",
  color: theme.palette.error.main,
}));

interface Option {
  value: string;
  label: string;
}

interface RadioGroupProps<TFieldValues extends FieldValues> {
  title?: string;
  parameterName: Path<TFieldValues>;
  register: UseFormRegister<TFieldValues>;
  control: Control<TFieldValues>;
  options: Option[];
  defaultValue?: string;
  helperText?: string;
  description?: string;
  // Read by the field's name, as a key of the form's errors.
  errors: FieldErrors;
  errorText?: string;
}

export const RadioInput = <TFieldValues extends FieldValues>({
  title,
  parameterName,
  register,
  control,
  options,
  defaultValue = "",
  helperText,
  description,
  errors,
  errorText,
}: RadioGroupProps<TFieldValues>) => {
  return (
    <RadioContainer>
      {title && <InputLabel>{title}</InputLabel>}
      {helperText && <HelperText>{helperText}</HelperText>}
      <Controller
        name={parameterName}
        control={control}
        // The field holds one of the options' values, which are strings.
        defaultValue={defaultValue as PathValue<TFieldValues, Path<TFieldValues>>}
        render={({ field }) => (
          <PaddedRadioGroup
            {...register(parameterName, { required: errorText ?? false })}
            {...field}
          >
            {options.map(({ value, label }) => (
              <FormControlLabel
                key={`${value}-${label}`}
                value={value}
                control={<Radio size="small" />}
                label={label}
              />
            ))}
            <RelativeContainer>
              {Boolean(errors[parameterName]) && (
                <ErrorMessage>
                  {errors[parameterName]?.message?.toString() ??
                    "Debes seleccionar una opción"}
                </ErrorMessage>
              )}
            </RelativeContainer>
          </PaddedRadioGroup>
        )}
      />
      {description && <Description>{description}</Description>}
    </RadioContainer>
  );
};
